import { useLocation, useMatch, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import {
  CalendarRange,
  ChevronsRight,
  CircleEllipsis,
  ClipboardList,
  FileText,
  Grid3x3,
  House,
  LayoutDashboard,
  MessagesSquare,
  Orbit,
  PenTool,
  Target,
  Timer,
  Users,
  Video,
  type IconProps,
} from '@/components/ui/icons';
import { cloneElement, isValidElement, useEffect, useState, type ReactNode } from 'react';
import { create } from 'zustand';
import { useDb, useMe } from '@/lib/store';
import { useWorkspaceId } from '@/lib/nav';
import { useUi, type RailSection } from '@/lib/ui';
import { Popover } from '@/components/ui/Popover';
import { Menu, MenuItem, MenuLabel, MenuSeparator } from '@/components/ui/Menu';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { RailTools } from './RailTools';

export const useRail = create<{ section: RailSection }>(() => ({ section: 'home' }));

interface RailItemDef {
  key: RailSection;
  label: string;
  icon: ReactNode;
  path?: string;
}

// Shown on the rail: sections that work today.
const ITEMS: RailItemDef[] = [
  { key: 'spaces', label: 'Spaces', icon: <Orbit size={20} /> },
  { key: 'chat', label: 'Chat', icon: <MessagesSquare size={20} />, path: 'chat' },
  { key: 'planner', label: 'Planner', icon: <CalendarRange size={20} />, path: 'planner' },
  { key: 'teams', label: 'Teams', icon: <Users size={20} />, path: 'teams' },
  { key: 'docs', label: 'Docs', icon: <FileText size={20} />, path: 'docs' },
];

// Under "More": the remaining working sections, then the coming-soon placeholders.
const MORE_ITEMS: RailItemDef[] = [
  { key: 'timesheets', label: 'Timesheet', icon: <Timer size={20} />, path: 'timesheets' },
  { key: 'everything', label: 'Everything', icon: <Grid3x3 size={20} />, path: 'everything' },
];

const SOON_ITEMS: RailItemDef[] = [
  { key: 'dashboards', label: 'Dashboards', icon: <LayoutDashboard size={20} />, path: 'dashboards' },
  { key: 'whiteboards', label: 'Whiteboards', icon: <PenTool size={20} />, path: 'whiteboards' },
  { key: 'forms', label: 'Forms', icon: <ClipboardList size={20} />, path: 'forms' },
  { key: 'clips', label: 'Clips', icon: <Video size={20} />, path: 'clips' },
  { key: 'goals', label: 'Goals', icon: <Target size={20} />, path: 'goals' },
];

/**
 * The page you're on when it isn't on the rail (it's under More, or opened from the Home sidebar): it gets
 * a rail button of its own just above More while you're there. Everything inside Spaces ("All Tasks")
 * belongs to Spaces instead.
 */
function useOverflowHere(section: RailSection): RailItemDef | null {
  const seg = useLocation().pathname.split('/')[2] ?? '';
  return [...MORE_ITEMS, ...SOON_ITEMS].find((i) => i.path === seg && !(i.key === 'everything' && section === 'spaces')) ?? null;
}

/** The active section shows the solid version of its icon. */
const railIcon = (icon: ReactNode, active: boolean) => (active && isValidElement<IconProps>(icon) ? cloneElement(icon, { fill: 'currentColor' }) : icon);

export function useInboxUnread() {
  const me = useMe();
  return useDb((db) => {
    let n = 0;
    for (const x of Object.values(db.notifications)) if (x.userId === me && !x.readAt && !x.clearedAt && !x.laterAt && x.bucket === 'primary') n++;
    return n;
  });
}

export function Rail() {
  const navigate = useNavigate();
  const ws = useWorkspaceId();
  const section = useRail((s) => s.section);
  const sidebarCollapsed = useUi((s) => s.sidebarCollapsed);
  // Settings has its own navigation: no sidebar there, so no expand button either
  const inSettings = useMatch('/:ws/settings') !== null;
  const showExpand = sidebarCollapsed && !inSettings;
  const unread = useInboxUnread();
  // a page from More: its button slides in above More, and slides out again (kept while it does) when you leave
  const here = useOverflowHere(section);
  const [temp, setTemp] = useState(here);
  if (here && here !== temp) setTemp(here);
  useEffect(() => {
    if (here) return;
    const t = setTimeout(() => setTemp(null), 250);
    return () => clearTimeout(t);
  }, [here]);
  const isActive = (key: RailSection) => !here && section === key;

  const go = (item: RailItemDef) => {
    // clicking a rail item always reveals the sidebar again
    useUi.setState({ sidebarCollapsed: false });
    useRail.setState({ section: item.key });
    if (item.path) navigate(`/${ws}/${item.path}`);
  };

  return (
    <nav className="rail">
      <WorkspaceSwitcher />
      <div className={clsx('rail-expand-wrap', { shown: showExpand })} aria-hidden={!showExpand || undefined}>
        <div className="rail-expand-inner">
          <button
            className="rail-expand"
            tabIndex={showExpand ? undefined : -1}
            onClick={() => useUi.setState({ sidebarCollapsed: false })}
            title="Expand sidebar ( [ )"
            aria-label="Expand sidebar"
          >
            <ChevronsRight size={20} />
          </button>
          <span className="rail-sep" />
        </div>
      </div>
      <button
        className={clsx('rail-item', { active: isActive('home') })}
        onClick={() => {
          useUi.setState({ sidebarCollapsed: false });
          useRail.setState({ section: 'home' });
          navigate(`/${ws}/home`);
        }}
      >
        <span className="rail-icon">
          <House size={20} fill={isActive('home') ? 'currentColor' : undefined} />
          {unread > 0 && <span className="rail-badge">{unread > 99 ? '99+' : unread}</span>}
        </span>
        <span className="rail-label">Home</span>
      </button>
      {ITEMS.map((item) => (
        <button key={item.key} className={clsx('rail-item', { active: isActive(item.key) })} onClick={() => go(item)}>
          <span className="rail-icon">{railIcon(item.icon, isActive(item.key))}</span>
          <span className="rail-label">{item.label}</span>
        </button>
      ))}
      <div className={clsx('rail-temp', { shown: !!here })} aria-hidden={!here || undefined}>
        <div className="rail-temp-inner">
          {temp && (
            <button className="rail-item active" tabIndex={here ? undefined : -1} onClick={() => go(temp)}>
              <span className="rail-icon">{railIcon(temp.icon, true)}</span>
              <span className="rail-label">{temp.label}</span>
            </button>
          )}
        </div>
      </div>
      <Popover
        placement="right-start"
        content={(close) => (
          <Menu>
            {MORE_ITEMS.map((item) => (
              <MenuItem key={item.key} icon={item.icon} label={item.label} onClick={() => (close(), go(item))} />
            ))}
            <MenuSeparator />
            <MenuLabel>Coming soon</MenuLabel>
            {SOON_ITEMS.map((item) => (
              <MenuItem key={item.key} icon={item.icon} label={item.label} onClick={() => (close(), go(item))} />
            ))}
          </Menu>
        )}
      >
        <button className="rail-item">
          <span className="rail-icon">
            <CircleEllipsis size={20} />
          </span>
          <span className="rail-label">More</span>
        </button>
      </Popover>
      <div className="spacer" />
      <RailTools />
    </nav>
  );
}
